<template>
    <div class="legal-page">
        <div class="legal-container">
            <header class="legal-header">
                <router-link to="/login" class="back-link">
                    <i class="bi bi-arrow-left"></i> Back
                </router-link>
                <TenantLogo id="legal-logo" logo-class="legal-logo" />
                <h1>Delete Your Account &amp; Data</h1>
                <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
            </header>

            <main class="legal-content">
                <section>
                    <h2>1. Overview</h2>
                    <p>
                        This page explains how you can request the deletion of your {{ clubName }} account and the
                        personal data associated with it in the {{ appName }} application. We respect your right to
                        erasure under {{ legal.privacyLaw }}.
                    </p>
                    <p>
                        Because accounts in {{ appName }} are managed by the club (and, for players under 18, by a
                        parent or guardian), account deletion is handled as a request that we action on your behalf.
                    </p>
                </section>

                <section>
                    <h2>2. How to Request Deletion</h2>
                    <p>To request deletion of your account and personal data, email us at the address below:</p>
                    <div class="contact-info">
                        <p><strong>{{ clubName }} — Data Requests</strong></p>
                        <p>
                            Email:
                            <a :href="deletionMailto">{{ deletionEmail }}</a>
                        </p>
                    </div>
                    <p>Please include the following so we can verify and locate your account:</p>
                    <ul>
                        <li>The full name on the account (and the player's name, if you are a parent or guardian)</li>
                        <li>The email address used to log in</li>
                        <li>The name of the team or age group, if known</li>
                    </ul>
                    <p>
                        We will confirm your identity before actioning any deletion to protect accounts from
                        unauthorised removal.
                    </p>
                </section>

                <section>
                    <h2>3. What Data Is Deleted</h2>
                    <p>Once your request is verified, we will delete the personal data we hold about you, including:</p>
                    <ul>
                        <li>Your profile and account login</li>
                        <li>Name, date of birth, contact details and address</li>
                        <li>Emergency contact and parent/guardian details</li>
                        <li>Medical and injury information</li>
                        <li>Performance, training, wellness and attendance data</li>
                        <li>Photographs and video footage linked to your profile</li>
                        <li>Messages and comments you have posted</li>
                    </ul>
                </section>

                <section>
                    <h2>4. What May Be Retained</h2>
                    <p>
                        We may retain a limited amount of information for a short period where we are legally required
                        to, or where it is necessary to resolve disputes, meet safeguarding obligations, or complete
                        financial and regulatory record‑keeping. Any retained data is kept only for as long as the law
                        requires and is then permanently deleted.
                    </p>
                </section>

                <section>
                    <h2>5. Children's Accounts</h2>
                    <p>
                        For players under 18, a parent or guardian may request deletion of their child's account and
                        data at any time using the email address above. We take additional care to verify parental
                        requests before actioning them.
                    </p>
                </section>

                <section>
                    <h2>6. Timeframe</h2>
                    <p>
                        We aim to acknowledge deletion requests within 7 days and to complete verified deletions within
                        30 days, in line with {{ legal.privacyLawShort }}. We will confirm by email once your data has
                        been deleted.
                    </p>
                </section>

                <section>
                    <h2>7. Contact Us</h2>
                    <p>If you have any questions about deleting your account or your data, please contact us:</p>
                    <div class="contact-info">
                        <p><strong>{{ clubName }}</strong></p>
                        <p>Email: <a :href="deletionMailto">{{ deletionEmail }}</a></p>
                    </div>
                </section>
            </main>

            <footer class="legal-footer">
                <p>&copy; {{ currentYear }} {{ clubName }}. All rights reserved.</p>
            </footer>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import TenantLogo from '@/components/TenantLogo/index.vue'
import { getLegalConfig } from '@/config/tenant'

export default {
    name: 'DeleteAccount',
    components: {
        TenantLogo
    },
    setup() {
        const store = useStore()

        const clubName = computed(() => store.getters.clubName || 'The Club')
        const appName = computed(() => store.getters.appName || 'Player App')

        const currentYear = new Date().getFullYear()
        const lastUpdated = 'July 2026'

        // Central data controller contact for deletion requests (all tenants).
        const deletionEmail = 'bigs@stewartpaxton.co.uk'
        const legal = getLegalConfig()
        const deletionMailto = computed(() =>
            `mailto:${deletionEmail}?subject=${encodeURIComponent('Data deletion request - ' + clubName.value)}`
        )

        return {
            clubName,
            appName,
            currentYear,
            lastUpdated,
            deletionEmail,
            deletionMailto,
            legal
        }
    }
}
</script>

<style lang="scss" scoped>
.legal-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 2rem;
}

.legal-container {
    max-width: 900px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.legal-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: white;
    padding: 2rem;
    text-align: center;
    position: relative;

    .back-link {
        position: absolute;
        left: 1.5rem;
        top: 1.5rem;
        color: white;
        text-decoration: none;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        opacity: 0.8;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }

    h1 {
        margin: 1rem 0 0.5rem;
        font-size: 2rem;
    }

    .last-updated {
        opacity: 0.7;
        font-size: 0.9rem;
    }
}

.legal-content {
    padding: 2rem 3rem;

    section {
        margin-bottom: 2rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    h2 {
        color: #1a1a2e;
        font-size: 1.4rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #e0e0e0;
    }

    h3 {
        color: #333;
        font-size: 1.1rem;
        margin: 1.5rem 0 0.75rem;
    }

    p {
        color: #555;
        line-height: 1.7;
        margin-bottom: 1rem;
    }

    a {
        color: #16213e;
        font-weight: 600;
    }

    ul {
        margin: 1rem 0;
        padding-left: 1.5rem;

        li {
            color: #555;
            line-height: 1.7;
            margin-bottom: 0.5rem;
        }
    }

    .contact-info {
        background: #f8f9fa;
        padding: 1.5rem;
        border-radius: 8px;
        margin-top: 1rem;

        p {
            margin-bottom: 0.5rem;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

.legal-footer {
    background: #f8f9fa;
    padding: 1.5rem;
    text-align: center;
    border-top: 1px solid #e0e0e0;

    p {
        color: #666;
        font-size: 0.9rem;
        margin: 0;
    }
}

#legal-logo {
    max-width: 120px;
    margin: 0 auto;
}

@media (max-width: 768px) {
    .legal-page {
        padding: 1rem;
    }

    .legal-content {
        padding: 1.5rem;
    }

    .legal-header {
        .back-link {
            position: static;
            margin-bottom: 1rem;
            justify-content: center;
        }
    }
}
</style>
